<template>
    <div
        :class="vertical ? 'flex-col' : 'flex-row items-center'"
        class="flex gap-4"
    >
        <div>
            <div
                :class="{'label-input-select main-text-color': edit }"
                class="description-field label-input"
            >
                {{ $t('general.gmtType') }}
            </div>
            <el-select
                v-model="gmtOffsetType"
                :placeholder="$t('general.sortBy')"
                size="small"
                class="select-type"
            >
                <el-option
                    v-for="option in gmtOffsetTypes"
                    :key="option.value"
                    v-bind="option"
                />
            </el-select>
        </div>
        <div
            v-if="gmtOffsetType === GmtOffsetTypes.Account"
            class="select-field"
        >
            <div
                :class="{'label-input-select main-text-color': edit }"
                class="description-field"
            >
                {{ $t('general.account') }}
            </div>
            <el-select
                v-model="account"
                :placeholder="$t('general.account')"
                size="small"
                class="select-type"
            >
                <el-option
                    v-for="option in allAccounts"
                    :key="option.AccountID"
                    :value="option.AccountID"
                    :label="option.dist_name"
                    v-bind="option"
                />
            </el-select>
        </div>
        <div
            v-if="gmtOffsetType === GmtOffsetTypes.DashboardTimezone"
            class="select-field"
        >
            <div
                :class="{'label-input-select main-text-color': edit }"
                class="description-field"
            >
                {{ $t('general.timezone') }}
            </div>
            <el-select
                v-model="gmtOffsetName"
                :placeholder="$t('general.timezone')"
                filterable
                size="small"
                class="select-type"
            >
                <el-option
                    v-for="option in timezones"
                    :key="option.value"
                    :value="option.value"
                    :label="option.label"
                />
            </el-select>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { Option as ElOption, Select as ElSelect } from 'element-ui'
import momentTimezone from 'moment-timezone'
import { GmtOffsetTypes } from '@/enum/gmt'
import i18n from '@/i18n'
import { TranslateResult } from 'vue-i18n'
import { entitiesData, selectedAccountData } from '@/composables/globalData/useEntitiesData'
import { GmtoffsetData } from '@/types/dashboard'

/* Types */
export interface IGMTOffsetData {
    Type: number
    AccountID?: number
    GMTOffset?: string
}
interface GMTOffsetType {
    label: TranslateResult
    value: number
}

/* Props */
const props = withDefaults(
    defineProps<{
        value?: GmtoffsetData
        edit?: boolean
        vertical?: boolean
    }>(),
    {
        value: undefined,
        edit: false,
        vertical: false
    }
)

/* Emit */
const emit = defineEmits<{
    (e: 'input', payload: IGMTOffsetData): void
    (e: 'update:value', payload: IGMTOffsetData): void
    (e: 'update:error', payload: string): void
}>()

/* Data */
const gmtOffsetTypes: Array<GMTOffsetType> = [
    {
        label: i18n.t('general.account'),
        value: GmtOffsetTypes.Account
    },
    {
        label: i18n.t('general.browser'),
        value: GmtOffsetTypes.Browser
    },
    {
        label: i18n.t('general.dashboardConf'),
        value: GmtOffsetTypes.DashboardTimezone
    }
]
const gmtOffsetType = ref<number>(props.value?.Type || GmtOffsetTypes.Browser)
const account = ref<undefined | number>(undefined)
const gmtOffsetName = ref<undefined | string>(undefined)
const timezones = momentTimezone.tz.names().map(tz => {
        const tzInstance = momentTimezone.tz(tz)
        return {
            value: tz,
            label: ` (GMT${tzInstance.format('Z')}) ${tz}`,
            offset: tzInstance.utcOffset()
        }
    })
    .sort((a, b) => {
        if (a.offset !== b.offset) {
            return a.offset - b.offset
        }
        return a.value.localeCompare(b.value)
    })

/* Computed */
const allAccounts = computed(() => {
    return entitiesData.value.Accounts
})
const GMTOffsetData = computed(() => {
    const data: IGMTOffsetData = {
        Type: gmtOffsetType.value
    }

    if (gmtOffsetType.value === GmtOffsetTypes.Account) {
        data.AccountID = account.value
    }
    if (gmtOffsetType.value === GmtOffsetTypes.DashboardTimezone) {
        data.GMTOffset = gmtOffsetName.value
    }

    return data
})
const typeOfError = computed(() => {
    let res = ''

    if (GMTOffsetData.value.Type === GmtOffsetTypes.Account) {
        if (GMTOffsetData.value.AccountID === undefined) {
            res = i18n.t('general.account') as string
        }
    } else if (GMTOffsetData.value.Type === GmtOffsetTypes.DashboardTimezone) {
        if (GMTOffsetData.value.GMTOffset === undefined) {
            res = i18n.t('general.timezone') as string
        }
    }

    return res
})

/* Watch */
watch(
    gmtOffsetType,
    (val) => {
        switch (val) {
            case GmtOffsetTypes.Browser:
                account.value = undefined
                gmtOffsetName.value = undefined

                break
            case GmtOffsetTypes.Account:
                gmtOffsetName.value = undefined

                if (selectedAccountData.value) {
                    account.value = selectedAccountData.value.AccountID
                }

                break
            case GmtOffsetTypes.DashboardTimezone:
                account.value = undefined
                gmtOffsetName.value = momentTimezone.tz.guess()

        }
    }
)
watch(
    GMTOffsetData,
    (val) => {
        if (val) {
            emit('update:value', val)
            emit('input', val)
            emit('update:error', typeOfError.value)
        }
    },
    {
        deep: true,
        immediate: true
    }
)

/* Created */
if (props.value?.Type === GmtOffsetTypes.Account) {
    account.value = props.value.AccountID
} else if (props.value?.Type === GmtOffsetTypes.DashboardTimezone) {
    gmtOffsetName.value = props.value.GMTOffset
}
</script>

<style>
.label-input-select {
    @apply flex text-sm font-normal truncate mb-3;
}
</style>
